/**
 * Short-service / pre-existing-condition flag.
 *
 * VA case law (38 CFR § 3.304(b), Wagner v. Principi) gives every veteran a
 * **presumption of soundness** when they entered service — but that
 * presumption is rebuttable by "clear and unmistakable evidence" that the
 * condition pre-existed entry AND was not aggravated by service.
 *
 * In practice, raters scrutinise the entrance exam / discharge exam delta
 * much harder when total service was very short, because:
 *
 *   • Fewer in-service treatment records exist.
 *   • The "gap" between entrance and discharge physicals is tighter.
 *   • Short separations (admin, medical, ELS) are correlated with claims
 *     where a pre-existing condition surfaced quickly.
 *
 * If total service length is under 6 months we surface a non-blocking
 * banner reminding the veteran to:
 *
 *   1. Consider whether the theory should be "Aggravation of pre-existing
 *      condition" rather than direct service-connection.
 *   2. Locate the entrance physical and the separation physical / Report
 *      of Medical History so the worsening can be quantified.
 *   3. Add a lay statement comparing pre- and post-service severity.
 *
 * The flag is purely informational — it never gates evidence, never
 * changes the pillar state, never silently rewrites the theory. The user
 * stays in control.
 *
 * Pure functions only. Reused by the Claim Tracker simple view, the Forms
 * view, and the PDF cover-letter notice block.
 */

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const SHORT_SERVICE_DAYS = 180; // 6 months
const VERY_SHORT_SERVICE_DAYS = 90; // 3 months — escalates wording

/**
 * Days BETWEEN serviceStart and serviceEnd. Returns null if either date is
 * missing or unparseable, or if serviceEnd is before serviceStart (bad
 * data → don't pretend we know the length).
 */
export function getServiceLengthDays(master526ez) {
  const startRaw = master526ez?.serviceStart;
  const endRaw = master526ez?.serviceEnd;
  if (!startRaw || !endRaw) return null;
  const start = new Date(startRaw);
  const end = new Date(endRaw);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diff = end.getTime() - start.getTime();
  if (diff < 0) return null;
  return Math.floor(diff / ONE_DAY_MS);
}

/** Approximate the day-count as whole months (30.44-day average). */
function asMonths(days) {
  if (days == null) return null;
  return Math.floor((days / 30.44) * 10) / 10; // one decimal
}

/**
 * Returns:
 *   {
 *     shortService: boolean,     // total < 6 months?
 *     veryShort:    boolean,     // total < 3 months? (worse scrutiny)
 *     days:         number|null,
 *     months:       number|null, // months, 1 decimal
 *     reason:       string|null, // user-facing prose, null when not flagged
 *     headline:     string|null,
 *     severity:     "info"|"warning"|null,
 *   }
 */
export function flagShortService(master526ez) {
  const days = getServiceLengthDays(master526ez);
  if (days === null) {
    return { shortService: false, veryShort: false, days: null, months: null, reason: null, headline: null, severity: null };
  }
  if (days >= SHORT_SERVICE_DAYS) {
    return { shortService: false, veryShort: false, days, months: asMonths(days), reason: null, headline: null, severity: null };
  }
  const veryShort = days < VERY_SHORT_SERVICE_DAYS;
  const months = asMonths(days);
  const headline = veryShort
    ? `Very short service (${months} months) — pre-existing condition scrutiny is likely.`
    : `Short service (${months} months) — check pre-existing-condition rules.`;
  const reason = veryShort
    ? `Your DD-214 shows under 3 months of service (${days} day${days === 1 ? "" : "s"}). VA raters apply the presumption of soundness, but very short separations (admin, medical, ELS) are reviewed closely. If this condition existed before service, you need to file under "Aggravation of pre-existing condition" and show worsening: entrance physical vs. separation physical, plus a lay statement quantifying the change.`
    : `Your DD-214 shows under 6 months of service (${days} days). The presumption of soundness still applies, but raters look harder at short-service claims. Pull your entrance physical AND your separation physical / Report of Medical History so any worsening is documented. If the condition pre-existed entry, switch the theory to "Aggravation of pre-existing condition."`;
  return {
    shortService: true,
    veryShort,
    days,
    months,
    reason,
    headline,
    severity: veryShort ? "warning" : "info",
  };
}
